import React from 'react';
import type { Nullable } from 'corgi-types';
import { SignupFlow } from 'corgi-types';

import { Link } from 'common/components/Link';
import { LinkRegularLR1 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { getUserFlowQueryParam } from 'common/utils/signup';

export interface LoginBackToLinkProps {
  userFlow: Nullable<SignupFlow>;
}

const TRACKING_EVENT_NAME = 'login-back-to-link_click';

const useBackToLinkOptions = (userFlow: Nullable<SignupFlow>) => {
  const { t, p } = useLocalization();

  switch (userFlow) {
    case null:
      return [t('h24_back_to_shop'), p(t('h24_home_url'))];
    case SignupFlow.Checkout:
      return [t('h24_back_to_login_page'), p(t('h24_single_page_checkout_url'))];
    case SignupFlow.GiftCardCheckout:
      return [t('h24_back_to_login_page'), p(t('h24_single_page_digital_checkout_url'))];
    case SignupFlow.Cart:
    case SignupFlow.Contact:
      return [
        t('h24_back_to_login_page'),
        `${p(t('h24_login_page_url'))}${getUserFlowQueryParam(userFlow)}`,
      ];
    default:
      return [t('h24_back_to_login_page'), p(t('h24_login_page_url'))];
  }
};

const LoginBackToLink = ({ userFlow }: LoginBackToLinkProps) => {
  const [linkLabel, linkHref] = useBackToLinkOptions(userFlow);

  return (
    <LinkRegularLR1
      data-corgi-id="login-back-to-link"
      color="sys.neutral.text.strong"
      textDecoration="underline"
    >
      <Link
        href={linkHref}
        tracking={{
          eventName: TRACKING_EVENT_NAME,
          text: linkLabel,
        }}
      >
        {linkLabel}
      </Link>
    </LinkRegularLR1>
  );
};

export default LoginBackToLink;
